import type { ComponentProps, HTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { Streamdown } from 'streamdown';

import { cn } from '@/lib/utils';
import type { Message as MessageType } from '@/types/assistant';

export const LoadingIndicator = ({ content = 'Thinking...' }: { content?: string }) => (
  <div className="flex items-center gap-2 text-muted-foreground text-xs">
    <span className="inline-block size-3 animate-pulse rounded-full bg-primary" />
    <span className="text-shimmer">{content}</span>
  </div>
);

export type MessageRole = Exclude<MessageType['role'], 'credit_notice'>;

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: MessageRole;
};

const messageVariants = cva('group flex w-full flex-col gap-2', {
  variants: {
    from: {
      user: 'is-user ml-auto max-w-[85%] justify-end',
      assistant: 'is-assistant max-w-[95%]',
      tool: 'is-assistant max-w-[95%]',
    },
  },
});

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div className={cn(messageVariants({ from }), className)} {...props} />
);

const messageContentVariants = cva(
  'is-user:dark flex w-fit max-w-full min-w-0 flex-col gap-2 text-sm',
  {
    variants: {
      from: {
        user: 'ml-auto rounded-2xl rounded-br-md bg-secondary px-4 py-2 text-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere]',
        assistant: 'text-foreground',
        tool: 'text-foreground',
      },
    },
  },
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement> & {
  from: MessageRole;
};

export const MessageContent = ({ children, className, from, ...props }: MessageContentProps) => (
  <div className={cn(messageContentVariants({ from }), className)} {...props}>
    {children}
  </div>
);

export const streamdownMarkdownClassName = [
  'prose prose-sm max-w-none text-pretty leading-relaxed',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  'prose-p:my-1 prose-headings:my-2',
  'prose-ol:my-1 prose-ul:my-1 prose-li:my-0',
  '[&_li>p]:my-0',
  '[&_ol]:list-outside [&_ol]:list-decimal [&_ol]:ps-4',
  '[&_ul]:list-outside [&_ul]:list-disc [&_ul]:ps-4',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto',
  '[&_[data-streamdown=heading-1]]:mt-2 [&_[data-streamdown=heading-1]]:mb-1 [&_[data-streamdown=heading-1]]:text-lg [&_[data-streamdown=heading-1]]:font-semibold',
  '[&_[data-streamdown=heading-2]]:mt-2 [&_[data-streamdown=heading-2]]:mb-1 [&_[data-streamdown=heading-2]]:text-base [&_[data-streamdown=heading-2]]:font-semibold',
  '[&_[data-streamdown=heading-3]]:text-sm [&_[data-streamdown=heading-3]]:font-medium',
  '[&_[data-streamdown^=heading-]]:mt-2 [&_[data-streamdown^=heading-]]:mb-1',
  '[&_[data-streamdown=horizontal-rule]]:my-3',
].join(' ');

export type MessageResponseProps = ComponentProps<typeof Streamdown> & {
  isLoading?: boolean;
};

export const MessageResponse = ({
  className,
  isLoading = false,
  children,
  isAnimating,
  ...props
}: MessageResponseProps) => {
  if (isLoading || !children) {
    return <LoadingIndicator />;
  }

  return (
    <Streamdown
      className={cn(streamdownMarkdownClassName, className)}
      parseIncompleteMarkdown={true}
      isAnimating={isAnimating ?? isLoading}
      caret="block"
      {...props}
      linkSafety={{ enabled: false }}
    >
      {children}
    </Streamdown>
  );
};

MessageResponse.displayName = 'MessageResponse';
